import { useLocation } from "react-router";
import routes from "../app/routes";
import { useFormatMessage } from "./intlHooks";
import { useYearPart } from "./yearPartHelpers";

export const YearSwitchBannerRoutes = [
  routes.drugSearchPreferences,
  routes.lisQuestions,
  routes.managePrescriptions,
  routes.pdePage,
  routes.pharmacy,
  routes.prescriptionsList,
  routes.questionRouting,
  routes.sanctionedPlans,
  routes.searchResults,
  routes.summary.drugSearchPreferences,
  routes.summary.lisQuestions,
  routes.summary.managePharmacies,
  routes.summary.managePrescriptions,
  routes.summary.pdePage,
  routes.summary.searchResults,
];

/**
 * useYearSwitchBanner returns a boolean to indicate whether or not to display the YearSwitchBanner
 * @returns true if to display the YearSwitchBanner, otherwise false
 */
export const useYearSwitchBanner = (): boolean => {
  const { isPublicPreview, isOpenEnrollment } = useYearPart();
  const location = useLocation();
  const showBanner = YearSwitchBannerRoutes.includes(location.pathname);

  return !!(isPublicPreview || isOpenEnrollment) && showBanner;
};

/**
 * addYearInTitle adds the year in the title if YearSwitchBanner is shown.
 * @param param object containing parameters
 * @param param.title the page title without the year
 * @param param.yearSwitchBannerShown indicates if YearSwitchBanner is shown or not
 * @param param.yearInfo the text to append to the title if YearSwitchBanner is shown.
 * @returns the title or the title with the year appended.
 */
export const addYearInTitle = ({
  title,
  yearSwitchBannerShown,
  yearInfo,
}: {
  title: string;
  yearSwitchBannerShown: boolean;
  yearInfo: string;
}): string => {
  return yearSwitchBannerShown && !!yearInfo ? `${title} ${yearInfo}` : title;
};

/**
 * useShouldAddYearToTitle returns information about whether to show the YearSwitchBanner or not and the text to append to the title if YearSwitchBanner is shown.
 * @param currentYear the year selected by the user (i.e. stored in state)
 * @returns the showYearSwitchBanner flag and the text to append to the title if YearSwitchBanner is shown
 */
export const useShouldAddYearToTitle = (
  currentYear?: string
): { showYearSwitchBanner: boolean; yearInfo: string } => {
  const showYearSwitchBanner = useYearSwitchBanner();

  const fm = useFormatMessage();
  const yearInfo = currentYear
    ? fm({ id: "page_title.year" }, { year: currentYear })
    : "";

  return { showYearSwitchBanner, yearInfo };
};

/**
 * useYearInTitle adds the current year to the title if YearSwitchBanner is shown.
 * @param title the page title without the year
 * @param currentYear the year selected by the user (i.e. stored in state)
 * @returns the title or the title with the year appended.
 */
export const useYearInTitle = (title: string, currentYear?: string): string => {
  const { showYearSwitchBanner, yearInfo } = useShouldAddYearToTitle(
    currentYear
  );

  return addYearInTitle({
    title,
    yearSwitchBannerShown: showYearSwitchBanner,
    yearInfo,
  });
};
